import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { GripVertical, User, X } from 'lucide-react';
import type { Player } from '../../types';
import { InjuryBadge } from '../ui/InjuryBadge';
import type { InjuryStatus } from '../../constants/injuries';

export interface BaseballLineupGridStats {
  batting_hand?: 'L' | 'R' | 'S';
  batting_average?: number;
  on_base_percentage?: number;
}

/** Optional metadata passed with `onPlayerChange` from the batting-order grid. */
export type LineupSlotChangeMeta = {
  /** When clearing a slot from "Clear this spot", compact higher slots down (no holes). */
  compactAfterClear?: boolean;
};

export interface BaseballLineupGridProps {
  /** Lineup slots: array of { order (1-based), player or null } for each slot */
  lineup: Array<{ order: number; player: Player | null }>;
  allPlayers: Player[];
  onPlayerChange: (order: number, playerId: string | null, meta?: LineupSlotChangeMeta) => void;
  /**
   * If set, invoked before assigning a player to a slot (picker or drag-drop).
   * Call `proceed()` to run the assignment (swap / move semantics).
   */
  interceptAssign?: (order: number, playerId: string, proceed: () => void) => void;
  playerStatsMap?: Map<string, { batting_average?: number; on_base_percentage?: number; batting_hand?: 'L' | 'R' | 'S' }>;
  /** Number of batting order slots to show (e.g. 9). Default 9. */
  slotCount?: number;
  /** Optional action to render at the end of each row (e.g. Edit stats button). Called with (order, player). */
  renderRowAction?: (order: number, player: Player | null) => React.ReactNode;
  /** Optional header content (e.g. "Drag players to set the batting order.") */
  headerSubtitle?: React.ReactNode;
}

const formatStat = (value?: number): string => {
  if (value === undefined || value === null) return '---';
  return value.toFixed(3).replace(/^0+/, '');
};

/** Narrow screens: tap-to-pick modal instead of drag-and-drop (better touch scrolling). */
export function useNarrowViewport(maxWidthPx = 768) {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(`(max-width: ${maxWidthPx}px)`).matches : false
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${maxWidthPx}px)`);
    const onChange = () => setMatches(mq.matches);
    mq.addEventListener('change', onChange);
    setMatches(mq.matches);
    return () => mq.removeEventListener('change', onChange);
  }, [maxWidthPx]);
  return matches;
}

function useEscapeDismiss(onDismiss: () => void, active: boolean) {
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, onDismiss]);
};

interface PlayerCardProps {
  player: Player;
  stats?: BaseballLineupGridStats;
  isDragging?: boolean;
  dragEnabled?: boolean;
  onDragStart: (e: React.DragEvent | React.TouchEvent, player: Player) => void;
  onDragEnd: () => void;
  onTouchStart?: (e: React.TouchEvent, player: Player) => void;
  onTouchMove?: (e: React.TouchEvent) => void;
  onTouchEnd?: () => void;
}

const PlayerCard: React.FC<PlayerCardProps> = ({
  player,
  stats,
  isDragging,
  dragEnabled = true,
  onDragStart,
  onDragEnd,
  onTouchStart,
  onTouchMove,
  onTouchEnd
}) => (
  <div
    draggable={dragEnabled}
    onDragStart={dragEnabled ? (e) => onDragStart(e, player) : undefined}
    onDragEnd={dragEnabled ? onDragEnd : undefined}
    onTouchStart={dragEnabled ? (e) => onTouchStart?.(e, player) : undefined}
    onTouchMove={dragEnabled ? onTouchMove : undefined}
    onTouchEnd={dragEnabled ? onTouchEnd : undefined}
    className={`
      flex items-center gap-2 p-2 border rounded transition-all min-w-0
      ${dragEnabled ? 'cursor-move touch-none' : 'cursor-pointer'}
      ${isDragging
        ? 'opacity-50 border-primary bg-primary/10'
        : 'border-gray-200 hover:border-primary hover:shadow-sm bg-white'
      }
    `}
  >
    {dragEnabled && <GripVertical className="w-4 h-4 text-gray-400 flex-shrink-0" />}
    <div className="flex items-center gap-2 flex-1 min-w-0 overflow-hidden">
      {player.number != null ? (
        <span className="font-bold text-primary text-sm flex-shrink-0 min-w-[2rem]">#{player.number}</span>
      ) : (
        <User size={16} className="text-gray-400 flex-shrink-0" />
      )}
      {/* Name + injury stay together; name truncates so badge does not overlap stats */}
      <div className="flex items-center gap-1 min-w-0 flex-1 overflow-hidden">
        <span className="font-medium text-xs truncate min-w-0">
          {player.first_name?.charAt(0) ?? ''}. {player.last_name ?? ''}
        </span>
        {player.injury_status && player.injury_status !== 'H' && (
          <InjuryBadge
            injuryStatus={player.injury_status as InjuryStatus}
            size="xs"
            className="flex-shrink-0"
          />
        )}
      </div>
    </div>
    <div className="flex items-center gap-1 text-xs text-gray-600 flex-shrink-0">
      <div className="flex flex-col items-center min-w-[2rem]">
        <span className="text-[9px] text-gray-500 uppercase">Bats</span>
        <span className="font-semibold text-xs text-primary">
          {stats?.batting_hand === 'S' ? 'S' : stats?.batting_hand === 'L' ? 'L' : stats?.batting_hand === 'R' ? 'R' : '---'}
        </span>
      </div>
      <div className="flex flex-col items-center min-w-[2rem]">
        <span className="text-[9px] text-gray-500 uppercase">Avg</span>
        <span className="font-semibold text-xs">{formatStat(stats?.batting_average)}</span>
      </div>
      <div className="flex flex-col items-center min-w-[2rem]">
        <span className="text-[9px] text-gray-500 uppercase">OBP</span>
        <span className="font-semibold text-xs">{formatStat(stats?.on_base_percentage)}</span>
      </div>
    </div>
  </div>
);

interface BattingOrderRowProps {
  order: number;
  player: Player | null;
  stats?: BaseballLineupGridStats;
  isDragging: boolean;
  draggedPlayer: Player | null;
  dragOverOrder: number | null;
  modalFlow: boolean;
  onSlotTap?: (order: number) => void;
  onDragOver: (e: React.DragEvent | React.TouchEvent, order: number) => void;
  onDrop: (e: React.DragEvent | React.TouchEvent, order: number) => void;
  onDragStart: (e: React.DragEvent | React.TouchEvent, player: Player) => void;
  onDragEnd: () => void;
  onTouchStart?: (e: React.TouchEvent, player: Player) => void;
  onTouchMove?: (e: React.TouchEvent) => void;
  onTouchEnd?: () => void;
  rowAction?: React.ReactNode;
}

const BattingOrderRow: React.FC<BattingOrderRowProps> = ({
  order,
  player,
  stats,
  isDragging,
  draggedPlayer,
  dragOverOrder,
  modalFlow,
  onSlotTap,
  onDragOver,
  onDrop,
  onDragStart,
  onDragEnd,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  rowAction
}) => {
  const isDragOver = !modalFlow && draggedPlayer && dragOverOrder === order && draggedPlayer.id !== player?.id;

  const slotActivate = () => {
    if (modalFlow) onSlotTap?.(order);
  };

  const slotProps = modalFlow
    ? {
        role: 'button' as const,
        tabIndex: 0,
        onClick: slotActivate,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            slotActivate();
          }
        }
      }
    : {};

  return (
    <div
      data-order={order}
      onDragOver={modalFlow ? undefined : (e) => onDragOver(e, order)}
      onDrop={modalFlow ? undefined : (e) => onDrop(e, order)}
      onTouchMove={
        modalFlow
          ? undefined
          : (e) => {
              if (draggedPlayer) {
                const touch = e.touches[0];
                const element = document.elementFromPoint(touch.clientX, touch.clientY);
                let dropZone = element;
                while (dropZone && dropZone.getAttribute('data-order') !== order.toString()) {
                  dropZone = dropZone.parentElement;
                  if (!dropZone || dropZone === document.body) break;
                }
                if (dropZone?.getAttribute('data-order') === order.toString()) {
                  onDragOver(e, order);
                }
              }
            }
      }
      className={`
        flex items-center gap-3 p-2 transition-all relative
        ${isDragOver ? 'bg-primary/10 border-2 border-dashed border-primary rounded-lg' : ''}
      `}
    >
      {isDragOver && (
        <div className="absolute inset-0 border-2 border-dashed border-primary bg-primary/5 rounded-lg pointer-events-none z-10" />
      )}
      <span className={`text-sm font-semibold min-w-[2rem] z-20 relative ${isDragOver ? 'text-primary' : 'text-gray-700'}`}>
        {order})
      </span>
      {player ? (
        <div
          className={`flex-1 min-w-0 z-20 relative ${modalFlow ? 'rounded-lg' : ''}`}
          {...slotProps}
        >
          <PlayerCard
            player={player}
            stats={stats}
            isDragging={isDragging}
            dragEnabled={!modalFlow}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          />
        </div>
      ) : (
        <div
          className={`flex-1 border-2 border-dashed rounded p-2 text-center text-xs bg-gray-50 z-20 relative transition-all ${
            isDragOver ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-gray-300 text-gray-400'
          } ${modalFlow ? 'cursor-pointer active:bg-gray-100' : ''}`}
          {...slotProps}
        >
          {modalFlow ? 'Tap to assign player' : isDragOver ? 'Drop here' : 'Drop player here'}
        </div>
      )}
      {rowAction != null && <div className="flex-shrink-0 z-20 relative">{rowAction}</div>}
    </div>
  );
};

export const BaseballLineupGrid: React.FC<BaseballLineupGridProps> = ({
  lineup,
  allPlayers,
  onPlayerChange,
  interceptAssign,
  playerStatsMap,
  slotCount = 9,
  renderRowAction,
  headerSubtitle
}) => {
  const modalFlow = useNarrowViewport(768);
  const [pickerOrder, setPickerOrder] = useState<number | null>(null);
  const closePicker = useCallback(() => setPickerOrder(null), []);
  useEscapeDismiss(closePicker, modalFlow && pickerOrder != null);
  const [draggedPlayer, setDraggedPlayer] = useState<Player | null>(null);
  const [dragOverOrder, setDragOverOrder] = useState<number | null>(null);
  const touchElementRef = useRef<HTMLElement | null>(null);

  const lineupByOrder = useMemo(() => {
    const map = new Map<number, Player | null>();
    lineup.forEach(({ order, player }) => map.set(order, player));
    return map;
  }, [lineup]);

  const orders = useMemo(() => Array.from({ length: slotCount }, (_, i) => i + 1), [slotCount]);

  const playersInLineup = useMemo(() => {
    return new Set(lineup.filter(e => e.player).map(e => e.player!.id));
  }, [lineup]);

  const unassignedPlayers = useMemo(() => {
    return allPlayers.filter(p => !playersInLineup.has(p.id));
  }, [allPlayers, playersInLineup]);

  const playersSortedForPicker = useMemo(() => {
    return [...allPlayers].sort((a, b) => {
      const ln = (a.last_name || '').localeCompare(b.last_name || '', undefined, { sensitivity: 'base' });
      if (ln !== 0) return ln;
      return (a.first_name || '').localeCompare(b.first_name || '', undefined, { sensitivity: 'base' });
    });
  }, [allPlayers]);

  const getPlayerStats = (playerId: string): BaseballLineupGridStats | undefined => {
    const player = allPlayers.find(p => p.id === playerId);
    if (!player) return undefined;
    if (playerStatsMap) {
      const fromMap = playerStatsMap.get(playerId);
      if (fromMap) {
        return {
          batting_hand: fromMap.batting_hand,
          batting_average: fromMap.batting_average,
          on_base_percentage: fromMap.on_base_percentage
        };
      }
    }
    return {
      batting_hand: (player as Player & { batting_hand?: 'L' | 'R' | 'S' }).batting_hand,
      batting_average: (player as Player & { batting_average?: number }).batting_average,
      on_base_percentage: (player as Player & { on_base_percentage?: number }).on_base_percentage
    };
  };

  const applyPlayerToOrder = (dropOrder: number, playerId: string | null) => {
    if (playerId === null) {
      onPlayerChange(dropOrder, null, { compactAfterClear: true });
      return;
    }
    const currentOrder = lineup.find(entry => entry.player?.id === playerId)?.order;
    const existingAtTarget = lineupByOrder.get(dropOrder) ?? null;

    if (existingAtTarget && existingAtTarget.id !== playerId) {
      if (currentOrder != null) onPlayerChange(currentOrder, existingAtTarget.id);
      onPlayerChange(dropOrder, playerId);
    } else if (!existingAtTarget) {
      if (currentOrder != null) onPlayerChange(currentOrder, null);
      onPlayerChange(dropOrder, playerId);
    }
  };

  const requestAssignToOrder = (dropOrder: number, playerId: string | null) => {
    if (playerId === null) {
      applyPlayerToOrder(dropOrder, null);
      return;
    }
    const run = () => applyPlayerToOrder(dropOrder, playerId);
    if (interceptAssign) {
      interceptAssign(dropOrder, playerId, run);
    } else {
      run();
    }
  };

  const handleDragStart = (e: React.DragEvent | React.TouchEvent, player: Player) => {
    setDraggedPlayer(player);
    if ('dataTransfer' in e) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/html', '');
    }
  };

  const handleDragEnd = () => {
    setDraggedPlayer(null);
    setDragOverOrder(null);
    touchElementRef.current = null;
  };

  const handleDragOver = (e: React.DragEvent | React.TouchEvent, order: number) => {
    if ('dataTransfer' in e) {
      // Mouse/desktop drag – allow drop
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
    }
    // For touch, we don't prevent default because React/browsers treat
    // touch listeners as passive; we only update visual drag-over state.
    setDragOverOrder(order);
  };

  const handleDrop = (e: React.DragEvent | React.TouchEvent, dropOrder: number) => {
    // Only prevent default for mouse/desktop drag events
    if ('dataTransfer' in e) {
      e.preventDefault();
    }
    setDragOverOrder(null);
    if (!draggedPlayer) return;

    const existingPlayer = lineupByOrder.get(dropOrder);

    if (existingPlayer && existingPlayer.id !== draggedPlayer.id) {
      requestAssignToOrder(dropOrder, draggedPlayer.id);
    } else if (!existingPlayer) {
      requestAssignToOrder(dropOrder, draggedPlayer.id);
    }
    setDraggedPlayer(null);
  };

  const handleTouchStart = (e: React.TouchEvent, player: Player) => {
    if (modalFlow) return;
    if (e.cancelable) {
      e.preventDefault();
    }
    setDraggedPlayer(player);
    touchElementRef.current = e.currentTarget as HTMLElement;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (modalFlow) return;
    if (!draggedPlayer) return;
    if (e.cancelable) {
      e.preventDefault();
    }
    const touch = e.touches[0];
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    let dropZone = element;
    while (dropZone && dropZone.getAttribute('data-order') == null) {
      dropZone = dropZone.parentElement;
      if (!dropZone || dropZone === document.body) break;
    }
    if (dropZone) {
      const order = parseInt(dropZone.getAttribute('data-order') ?? '0', 10);
      if (order > 0) setDragOverOrder(order);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (modalFlow) return;
    if (e.cancelable) {
      e.preventDefault();
    }
    if (dragOverOrder != null && draggedPlayer) {
      handleDrop(e, dragOverOrder);
    }
    handleDragEnd();
  };

  return (
    <div className="space-y-4">
      {headerSubtitle != null && (
        <p className="text-xs text-gray-600">{headerSubtitle}</p>
      )}
      <div className="space-y-1">
        {orders.map(order => {
          const player = lineupByOrder.get(order) ?? null;
          const stats = player ? getPlayerStats(player.id) : undefined;
          return (
            <div key={order} data-order={order}>
              <BattingOrderRow
                order={order}
                player={player}
                stats={stats}
                isDragging={draggedPlayer?.id === player?.id}
                draggedPlayer={draggedPlayer}
                dragOverOrder={dragOverOrder}
                modalFlow={modalFlow}
                onSlotTap={setPickerOrder}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                rowAction={renderRowAction?.(order, player)}
              />
            </div>
          );
        })}
      </div>
      {unassignedPlayers.length > 0 && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Unassigned Players</h4>
          {modalFlow ? (
            <p className="text-xs text-gray-600 mb-3">
              Tap a batting slot above to add a player from the list.
            </p>
          ) : null}
          <div className={modalFlow ? 'flex flex-wrap gap-2' : 'space-y-2'}>
            {unassignedPlayers.map(player => {
              const stats = getPlayerStats(player.id);
              return modalFlow ? (
                <span
                  key={player.id}
                  className="text-sm text-gray-600 px-2 py-1 bg-white rounded border border-gray-200"
                >
                  {player.number ? `#${player.number} ` : ''}
                  {player.first_name} {player.last_name}
                </span>
              ) : (
                <PlayerCard
                  key={player.id}
                  player={player}
                  stats={stats}
                  isDragging={draggedPlayer?.id === player.id}
                  dragEnabled
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                />
              );
            })}
          </div>
        </div>
      )}

      {modalFlow && pickerOrder != null && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          role="presentation"
          onClick={closePicker}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col"
            role="dialog"
            aria-modal="true"
            aria-labelledby="lineup-picker-title"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b shrink-0">
              <h3 id="lineup-picker-title" className="text-lg font-semibold pr-2">
                Batting order — #{pickerOrder}
              </h3>
              <button
                type="button"
                onClick={closePicker}
                className="text-gray-400 hover:text-gray-600 p-1"
                aria-label="Close"
              >
                <X size={24} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 min-h-0">
              <div className="space-y-2">
                {lineupByOrder.get(pickerOrder) ? (
                  <button
                    type="button"
                    onClick={() => {
                      applyPlayerToOrder(pickerOrder, null); // clears + compacts via meta flag
                      closePicker();
                    }}
                    className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-gray-600">Clear this spot</span>
                  </button>
                ) : null}
                {playersSortedForPicker.map(p => {
                  const orderElsewhere = lineup.find(e => e.player?.id === p.id)?.order;
                  const atThisSpot = lineupByOrder.get(pickerOrder)?.id === p.id;
                  const assignedElsewhere = orderElsewhere != null && orderElsewhere !== pickerOrder;

                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => {
                        requestAssignToOrder(pickerOrder, p.id);
                        closePicker();
                      }}
                      className={`
                        w-full text-left px-4 py-3 border rounded-lg transition-colors
                        ${atThisSpot
                          ? 'bg-primary text-white border-primary'
                          : assignedElsewhere
                            ? 'border-yellow-300 bg-yellow-50 hover:bg-primary hover:text-white hover:border-primary'
                            : 'border-gray-200 hover:bg-primary hover:text-white hover:border-primary'
                        }
                      `}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          {p.number ? (
                            <span className="font-semibold shrink-0">#{p.number}</span>
                          ) : (
                            <User size={16} className="text-gray-400 shrink-0" />
                          )}
                          <span className="truncate">
                            {p.first_name} {p.last_name}
                          </span>
                          {p.injury_status && p.injury_status !== 'H' && (
                            <InjuryBadge injuryStatus={p.injury_status as InjuryStatus} size="sm" />
                          )}
                        </div>
                        {assignedElsewhere && (
                          <span className="text-xs font-medium shrink-0">#{orderElsewhere}</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
